import { useDuplicateAnalysis } from '@modules/analysis/hooks/useDuplicateAnalysis';
import { useAnalysisStore } from '@modules/analysis/store/analysisStore';
import { useNotificationContext } from '@modules/common/components/Notification/NotificationContext';
import { useGetArtefactScope } from '@modules/common/hooks';
import { useGetQuantifyQueryMetadata } from '@modules/quantify/hooks';
import { useGetQuantifyTableEQLProps } from '@modules/quantify/hooks/useFetchQuantifyTableData/useGetQuantifyTableEQLProps';
import { useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useGetEQLExpression } from 'v2/core/repository/edge-server/eql/useGetEQLExpression';
import { getAnalysisRoute } from 'v2/core/services/analysis/link';
import useGetAnalysisDeps from 'v2/core/services/analysis/useGetAnalysisDeps';
import useFullscreenLoader from 'v2/core/services/loader/useFullscreenLoader';
import { shallow } from 'zustand/shallow';

export const useDuplicateQuantify = () => {
  const navigate = useNavigate();

  const { useCase, title, resetToEmptyAnalysis } = useAnalysisStore(
    (analysis) => ({
      useCase: analysis.useCase,
      title: analysis.title,
      resetToEmptyAnalysis: analysis.resetToEmptyAnalysis,
    }),
    shallow,
  );
  const artefactScope = useGetArtefactScope();

  const {
    data: { user, tenantID },
  } = useGetAnalysisDeps();
  const eqlProps = useGetQuantifyTableEQLProps();
  const queryMetadata = useGetQuantifyQueryMetadata();
  const { showErrorNotification } = useNotificationContext();
  const { eql } = useGetEQLExpression({
    tenantID,
    eqlProps,
  });
  const {
    createAnalysis: createQuantifyAnalysis,
    isLoading,
    error,
  } = useDuplicateAnalysis({
    eqlProps,
    metadata: queryMetadata,
  });

  useFullscreenLoader({ isLoading, isInvisible: true });

  useEffect(() => {
    if (error) {
      showErrorNotification({
        contentComponent: `Failed to create new Quantify.`,
        props: { isClosable: true },
      });
    }
  }, [error, showErrorNotification]);

  const handleDuplicateQuantify = useCallback(async () => {
    try {
      const name = `Copy of ${title}`;
      const savedAnalysis: any = await createQuantifyAnalysis({
        variables: {
          tenantID,
          title: name,
          scope: artefactScope,
          panels: [],
          metadata: queryMetadata,
          analysisExpresssion: eql ?? '',
          createdBy: user.id ?? '',
          useCase,
        },
      });
      if (savedAnalysis?.data?.createAnalysis) {
        const path = getAnalysisRoute({
          name,
          token: savedAnalysis.data.createAnalysis.viewToken,
        });
        navigate(path);
        resetToEmptyAnalysis();
      }
    } catch {
      showErrorNotification({
        contentComponent: `Failed to create new Quantify.`,
        props: { isClosable: true },
      });
    }
  }, [
    createQuantifyAnalysis,
    eql,
    navigate,
    queryMetadata,
    resetToEmptyAnalysis,
    artefactScope,
    showErrorNotification,
    tenantID,
    title,
    useCase,
    user.id,
  ]);

  return handleDuplicateQuantify;
};
